import LoadingDrawer from '@/shared/ui/components/loadingDrawer/loadingDrawer';
import CustomButton from '@/shared/ui/components/customButton';
import { closeIconV2 } from '@/shared/ui/assets';
import { Drawer } from 'antd';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useWithDrawerLayout } from './useWithDrawerLayout';

/**
 * Higher-order component mounting a component inside the shared side drawer. Returns a wrapper that defaults destroyOnClose, title, className, width and isLoading, reads open and scroll state from useWithDrawerLayout, renders a title bar whose close button also serves the loading overlay, and passes onCancel and toggleLoading down to the wrapped component.
 *
 * @param ChildrenComponent - Component rendered as the drawer body.
 * @returns Wrapper component rendering the drawer.
 */
const withDrawerLayout = (ChildrenComponent: any) => {
  /**
   * Renders the wrapped component inside the shared drawer shell. Defaults destroyOnClose, title, className, width and isLoading from props, drives open and scroll state through useWithDrawerLayout, wraps the body in LoadingDrawer with a close button, and adds a box-shadow class to the title bar while the body is scrolled.
   *
   * @param props - Drawer props, also forwarded to the wrapped component.
   * @returns The drawer element containing the wrapped component.
   */
  const WithDrawerLayout = (props: any) => {
    const {
      destroyOnClose = true,
      title = '',
      className = '',
      width = 500,
      isLoading = false,
    } = props;

    const { state, handleClose, toggleLoading, handleScroll } = useWithDrawerLayout(props);

    const closeButton = (
      <CustomButton
        id="close-btn"
        style="neutral"
        type="tertiary"
        size="small"
        icon={<ReactSVG src={closeIconV2} />}
        onClick={handleClose}
        ariaLabel={t`Close`}
      />
    );

    return (
      <Drawer
        id="with-drawer-layout-id"
        aria-label={typeof title === 'string' ? title : undefined}
        open={state.open}
        destroyOnHidden={destroyOnClose}
        closable={false}
        width={width}
        rootClassName={classNames(className, 'custom-drawer-v2')}
        onClose={handleClose}
      >
        <LoadingDrawer isLoading={isLoading} rightIcon={closeButton}>
          <div
            className={classNames(
              'drawer-title-container',
              state.isScroll ? '--has-box-shadow' : '',
            )}
          >
            <span className="drawer-title">{title}</span>

            {closeButton}
          </div>
          <div className="drawer-body-container" onScroll={handleScroll}>
            <ChildrenComponent {...props} onCancel={handleClose} toggleLoading={toggleLoading} />
          </div>
        </LoadingDrawer>
      </Drawer>
    );
  };

  return WithDrawerLayout;
};

export default withDrawerLayout;
